<template>
  <div class="content d-flex flex-column flex-column-fluid" id="kt_content">
    <div class="toolbar" id="kt_toolbar">
      <div id="kt_toolbar_container" class="container-fluid d-flex flex-stack flex-wrap gap-2">
        <div class="page-title d-flex flex-column me-3">
          <h1 class="d-flex align-items-center text-dark fw-bolder fs-3 my-1">{{ $t('login_log.title') }}</h1>
        </div>
        <div class="d-flex flex-wrap align-items-center gap-2">
          <input v-model="filters.search" type="search" class="form-control form-control-sm w-200px" :placeholder="$t('login_log.search')" @input="debouncedReload" />
          <select v-model="filters.actor" class="form-select form-select-sm w-175px" @change="reload">
            <option value="">{{ $t('login_log.all_accounts') }}</option>
            <option value="user">{{ $t('login_log.account_user') }}</option>
            <option value="admin">{{ $t('login_log.account_admin') }}</option>
          </select>
          <select v-model="filters.society_id" class="form-select form-select-sm w-200px" @change="reload">
            <option value="">{{ $t('login_log.all_societies') }}</option>
            <option v-for="society in societies" :key="society.id" :value="society.id">{{ society.name }}</option>
          </select>
          <select v-model="filters.event" class="form-select form-select-sm w-175px" @change="reload">
            <option value="">{{ $t('login_log.all_events') }}</option>
            <option v-for="event in events" :key="event" :value="event">{{ $t('login_log.events.' + event) }}</option>
          </select>
          <input v-model="filters.from" type="date" class="form-control form-control-sm w-150px" :title="$t('login_log.from')" @change="reload" />
          <input v-model="filters.to" type="date" class="form-control form-control-sm w-150px" :title="$t('login_log.to')" @change="reload" />
        </div>
      </div>
    </div>

    <div class="post d-flex flex-column-fluid" id="kt_post">
      <div id="kt_content_container" class="container-fluid">
        <div class="card card-flush">
          <div class="card-body py-4">
            <div v-if="loading" class="text-center py-10"><span class="spinner-border text-primary"></span></div>
            <div v-else-if="!logs.length" class="text-center py-10 text-gray-500">{{ $t('login_log.empty') }}</div>
            <div v-else class="table-responsive">
              <table class="table align-middle table-row-dashed fs-6 gy-3">
                <thead>
                  <tr class="text-start text-gray-500 fw-bold fs-7 text-uppercase gs-0">
                    <th>{{ $t('login_log.date') }}</th>
                    <th>{{ $t('login_log.account') }}</th>
                    <th>{{ $t('login_log.society') }}</th>
                    <th>{{ $t('login_log.event') }}</th>
                    <th>{{ $t('login_log.device') }}</th>
                    <th>{{ $t('login_log.ip') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="log in logs" :key="log.id">
                    <td class="text-nowrap fs-7">{{ formatDate(log.created_at) }}</td>
                    <td>
                      <div class="fw-bold">
                        {{ log.name || '—' }}
                        <span v-if="log.actor_type === 'admin'" class="badge badge-light-dark ms-1">{{ $t('login_log.landlord') }}</span>
                      </div>
                      <div class="text-muted fs-8">{{ log.identifier }}</div>
                    </td>
                    <td class="fs-7">
                      <a v-if="log.society_id" href="#" class="text-gray-800 text-hover-primary" @click.prevent="filterSociety(log.society_id)">{{ log.society_name || '—' }}</a>
                      <span v-else>—</span>
                      <div v-if="log.shop_name" class="text-muted fs-8">{{ log.shop_name }}</div>
                    </td>
                    <td><span class="badge" :class="eventClass(log.event)">{{ $t('login_log.events.' + log.event) }}</span></td>
                    <td class="fs-7">{{ log.device || '—' }}</td>
                    <td class="fs-7 text-muted">{{ log.ip || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="lastPage > 1" class="d-flex flex-wrap gap-3 justify-content-between align-items-center mt-5">
              <span class="text-muted fs-7">{{ $t('login_log.page', { page, last: lastPage, total }) }}</span>
              <PageNumbers :page="page" :last="lastPage" :disabled="loading" @change="go" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageNumbers from '@/components/PageNumbers.vue'
import landlordAxios from '@/services/landlordAxios'

/** Landlord : journal des connexions de toutes les sociétés et des comptes Landlord (90 jours). */
export default {
  name: 'LandlordLoginLogs',
  components: { PageNumbers },

  data() {
    return {
      events: ['login', 'login_failed', 'logout', 'logout_all', 'password_reset', 'password_changed'],
      filters: { search: '', actor: '', society_id: this.$route.query.society_id || '', event: '', from: '', to: '' },
      societies: [],
      logs: [],
      page: 1,
      lastPage: 1,
      total: 0,
      loading: true,
      timer: null,
    }
  },

  mounted() {
    landlordAxios.get('landlord/societies', { params: { per_page: 500 } }).then(response => {
      this.societies = (response.data.data || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    }).catch(() => {})
    this.load()
  },

  methods: {
    async load() {
      this.loading = true
      try {
        const params = { page: this.page }
        Object.entries(this.filters).forEach(([key, value]) => { if (value) params[key] = value })
        const { data } = await landlordAxios.get('landlord/login-logs', { params })
        this.logs = data.data
        this.lastPage = data.last_page
        this.total = data.total
      } finally {
        this.loading = false
      }
    },

    reload() {
      this.page = 1
      this.load()
    },

    debouncedReload() {
      clearTimeout(this.timer)
      this.timer = setTimeout(this.reload, 350)
    },

    go(page) {
      this.page = page
      this.load()
    },

    filterSociety(societyId) {
      this.filters.society_id = societyId
      this.reload()
    },

    eventClass(event) {
      return {
        login: 'badge-light-success',
        login_failed: 'badge-light-danger',
        logout: 'badge-light',
        logout_all: 'badge-light-warning',
        password_reset: 'badge-light-primary',
        password_changed: 'badge-light-primary',
      }[event] || 'badge-light'
    },

    formatDate(value) {
      return value ? new Date(value.replace(' ', 'T') + (value.includes('Z') || value.includes('+') ? '' : 'Z')).toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      }) : ''
    },
  },
}
</script>
